import { MUIDataTableOptions } from 'mui-datatables';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { AddEntityToolbarButton } from '../../common/table/AddEntityToolbarButton';
import {
  AcademicWorkDataFragment,
  SupportedLanguage,
  useDeleteAcademicWorkMutation,
} from '../../../graphql/models';
import { QUERY_ACADEMIC_WORKS } from '../../../graphql/queries/academic-work/academic-works';
import { getTranslatedTagNameOrDefault } from '../../../model/Tag';
import { QUERY_ACADEMIC_WORKS_TABLE } from '../../../graphql/queries/academic-work/academic-works-table';
import { GenericTable } from '../../common/table/GenericTable';
import React from 'react';
import DeleteConfirmationDialog from '../../common/table/DeleteConfirmationDialog';
import { RowsToBeDeleted } from '../../common/table/types';
import {
  ResourcesTableRow,
  ResourcesTableRowWithTags,
  useResourcesTableColumns,
} from '../../common/table/resources/resources-table-columns';
import { NavigationUtil } from '../../../common/navigation/NavigationUtil';
import { ResourcePath } from '../../../common/navigation/path-types';
import { useRoutes } from '../../../common/routing/routes-hook';
import { languageCompare } from '../../../common/supported-languages';

interface Props {
  academicWorks: AcademicWorkDataFragment[];
}

export const AcademicWorksTable = ({ academicWorks }: Props): JSX.Element => {
  const { i18n, t } = useTranslation();
  const { routes } = useRoutes();
  const history = useHistory();

  const [rowsToBeDeleted, setRowsToBeDeleted] = React.useState<
    RowsToBeDeleted | undefined
  >();

  const handleDeleteConfirmation = async () => {
    if (rowsToBeDeleted) {
      await deleteRows();
    }
    setRowsToBeDeleted(undefined);
  };

  const handleDeleteCancellation = () => {
    setRowsToBeDeleted(undefined);
  };

  const onAddNewClick = (): void => history.push(routes.academicWorksNew.path);

  const AddButton = (): JSX.Element => (
    <AddEntityToolbarButton
      tooltipText={t('phrases.addNewAcademicWork')}
      onClick={onAddNewClick}
    />
  );

  const resourcesTableDataWithTags: ResourcesTableRowWithTags[] =
    academicWorks.map(({ id, content, originalLanguage, tags, authors }) => {
      const title =
        content.find(({ language }) => languageCompare(language, i18n))
          ?.title ??
        content.find((value) => value.language === originalLanguage)?.title ??
        '-';

      const formattedAuthors = authors.map((author) => author.name);
      const languages = content
        .filter(({ title: translatedTitle }) => translatedTitle.length > 0)
        .map((translation) => translation.language)
        .sort();

      const publicLink = NavigationUtil.publicResourceLinkFrom(
        ResourcePath.academicWork,
        id,
        i18n.language as SupportedLanguage,
        languages
      );

      return {
        id,
        title,
        tags: tags || [],
        authors: formattedAuthors,
        languages,
        publicLink,
      };
    });

  const tableData: ResourcesTableRow[] = resourcesTableDataWithTags.map(
    ({ tags, ...restData }) => {
      const tagsRender = tags.map((tag) => {
        return getTranslatedTagNameOrDefault(
          tag,
          i18n.language as SupportedLanguage
        );
      });

      return {
        ...restData,
        tags: tagsRender,
      };
    }
  );

  const columns = useResourcesTableColumns(resourcesTableDataWithTags);

  const [deleteMutation] = useDeleteAcademicWorkMutation();

  const deleteRows = async () => {
    for (const row of rowsToBeDeleted?.data ?? []) {
      const id = academicWorks[row.dataIndex].id;
      await deleteMutation({
        variables: { id },
        refetchQueries: [
          { query: QUERY_ACADEMIC_WORKS },
          { query: QUERY_ACADEMIC_WORKS_TABLE },
        ],
      });
    }
  };

  const options: MUIDataTableOptions = {
    onRowsDelete: (rowsDeleted) => {
      setRowsToBeDeleted(rowsDeleted);
    },
    onRowClick(_, rowMeta: { dataIndex: number; rowIndex: number }) {
      history.push(
        routes.academicWorksEdit.pathWithArguments({
          id: String(academicWorks[rowMeta.dataIndex].id),
        })
      );
    },
    customToolbar: AddButton,
    sortOrder: {
      name: 'id',
      direction: 'asc',
    },
  };

  return (
    <>
      <DeleteConfirmationDialog
        handleDeleteConfirmation={handleDeleteConfirmation}
        handleDeleteCancellation={handleDeleteCancellation}
        open={rowsToBeDeleted !== undefined}
      />
      <GenericTable
        title={t('navigation.academicWorks')}
        data={tableData}
        columns={columns}
        options={options}
      />
    </>
  );
};
